import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Palette, Sparkles } from 'lucide-react';
import { brandColors } from '../data/brandColors';

export const DesignSystemArchive: React.FC = () => {
  // Utility to pick readable text color (black/white) over any hex background
  const getReadableTextColor = (hex: string): string => {
    const normalized = hex.replace('#', '');
    const r = parseInt(normalized.substring(0, 2), 16) / 255;
    const g = parseInt(normalized.substring(2, 4), 16) / 255;
    const b = parseInt(normalized.substring(4, 6), 16) / 255;
    const [R, G, B] = [r, g, b].map(v => {
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
    return luminance > 0.5 ? '#111827' : '#FFFFFF'; // charcoal-900 on light, white on dark
  };

  type SwatchProps = {
    hex: string;
    title: string;
    ringClassName?: string;
  };

  const Swatch: React.FC<SwatchProps> = ({ hex, title, ringClassName }) => (
    <div
      className={`flex-1 h-12 rounded relative ${ringClassName ?? ''}`}
      style={{ backgroundColor: hex }}
      title={title}
    >
      <span
        className="absolute inset-0 flex items-center justify-center text-[10px] sm:text-xs font-mono"
        style={{ color: getReadableTextColor(hex) }}
      >
        {hex}
      </span>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/design-language" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Design System
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-consulting-slate-blue-100 text-consulting-slate-blue-800">
            Design System Archive
          </div>
          <Palette className="h-10 w-10 text-consulting-slate-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Design System Home Page Archive</h1>
            <p className="text-charcoal-600">Archived content from the original design system home page</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          This archive contains the detailed design system content that was previously on the main design system page.
        </p>
      </div>

      {/* Visual Design Language */}
      <div className="space-y-8">
        {/* Introduction */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 p-8">
          <div className="flex items-center gap-3 mb-4">
            <Palette className="h-6 w-6 text-[var(--mustard)]" />
            <h2 className="text-2xl font-bold text-charcoal-900">Visual Design Language</h2>
          </div>
          <p className="text-charcoal-700 leading-relaxed mb-6">
            Our visual design language creates instant recognition and conceptual clarity across all Peak Priorities assets. 
            Every color, icon, and layout pattern reinforces the five-layer MOF architecture and the Meta-Stack grammar.
          </p>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <p className="text-[var(--mustard)] font-medium">
              <strong>Design Principle:</strong> Visual elements should make the abstract architecture feel intuitive and navigable, not more complex.
            </p>
          </div>
        </div>

        {/* Peak Priorities Brand Color Palette */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 p-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Brand Color Palette</h2>
          <p className="text-charcoal-700 mb-6">
            Our core brand colors with full tint/shade ramps. Each family includes anchor points for consistency across all deliverables.
          </p>
          
          <div className="space-y-6">
            {/* Slate Blue — chromatic Peak consulting */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-charcoal-900">Slate Blue (consulting structural)</h3>
                  <p className="text-sm text-charcoal-600">Chromatic brand blue — Anchor #3D4B6F (Tailwind consulting-slate-blue-500/600)</p>
                </div>
                <code className="text-sm font-mono bg-charcoal-100 px-3 py-1 rounded">#3D4B6F</code>
              </div>
              <div className="flex gap-1">
                <Swatch hex="#FFFFFF" title="consulting-slate-0" />
                <Swatch hex="#E8F1F9" title="consulting-slate-50" />
                <Swatch hex="#C6D2E2" title="consulting-slate-100" />
                <Swatch hex="#9FAEC9" title="consulting-slate-200" />
                <Swatch hex="#64789E" title="consulting-slate-300" />
                <Swatch hex="#51618A" title="consulting-slate-400" />
                <Swatch hex="#3D4B6F" title="consulting-slate-500/600 (anchor)" ringClassName="ring-4 ring-consulting-slate-blue-300" />
                <Swatch hex="#313845" title="consulting-slate-700" />
                <Swatch hex="#2B2F32" title="consulting-slate-800" />
                <Swatch hex="#191A1C" title="consulting-slate-900" />
              </div>
            </div>

            {/* Sage / light UI primary — same as Tailwind slate-blue */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-charcoal-900">{brandColors.primary.name} (tw: slate-blue)</h3>
                  <p className="text-sm text-charcoal-600">Prioritization app + light elevation — Anchor at 70%</p>
                </div>
                <code className="text-sm font-mono bg-charcoal-100 px-3 py-1 rounded">#5A7D5E</code>
              </div>
              <div className="flex gap-1">
                <Swatch hex="#FFFFFF" title="sage-slate-0" />
                <Swatch hex="#F2F7F3" title="sage-slate-50" />
                <Swatch hex="#D9E8DC" title="sage-slate-100" />
                <Swatch hex="#B5D1BA" title="sage-slate-200" />
                <Swatch hex="#84B18C" title="sage-slate-300" />
                <Swatch hex="#6A9C73" title="sage-slate-400" />
                <Swatch hex="#5A7D5E" title="sage-slate-500/600 (anchor)" ringClassName="ring-4 ring-slate-blue-300" />
                <Swatch hex="#465F49" title="sage-slate-700" />
                <Swatch hex="#354738" title="sage-slate-800" />
                <Swatch hex="#1E2A20" title="sage-slate-900" />
              </div>
            </div>

            {/* Alpine Green */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-charcoal-900">Alpine Green</h3>
                  <p className="text-sm text-charcoal-600">Secondary brand color - Anchor at 80%</p>
                </div>
                <code className="text-sm font-mono bg-charcoal-100 px-3 py-1 rounded">#53675B</code>
              </div>
              <div className="flex gap-1">
                <Swatch hex="#FFFFFF" title="alpine-0" />
                <Swatch hex="#E8F4EE" title="alpine-10" />
                <Swatch hex="#C6E1D2" title="alpine-20" />
                <Swatch hex="#A3CCB5" title="alpine-30" />
                <Swatch hex="#73AA8C" title="alpine-50" />
                <Swatch hex="#5E9876" title="alpine-60" />
                <Swatch hex="#497962" title="alpine-70" />
                <Swatch hex="#53675B" title="alpine-80 (anchor)" ringClassName="ring-4 ring-sage-400" />
                <Swatch hex="#3A4A3F" title="alpine-90" />
                <Swatch hex="#2A332C" title="alpine-100" />
              </div>
            </div>

            {/* Electric Mustard */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-charcoal-900">Electric Mustard</h3>
                  <p className="text-sm text-charcoal-600">Accent color - Anchor at 60%</p>
                </div>
                <code className="text-sm font-mono bg-charcoal-100 px-3 py-1 rounded">#D4A574</code>
              </div>
              <div className="flex gap-1">
                <Swatch hex="#FFFFFF" title="mustard-0" />
                <Swatch hex="#FDF8F2" title="mustard-10" />
                <Swatch hex="#F9E8D4" title="mustard-20" />
                <Swatch hex="#F4D5B0" title="mustard-30" />
                <Swatch hex="#E8B885" title="mustard-50" />
                <Swatch hex="#D4A574" title="mustard-60 (anchor)" ringClassName="ring-4 ring-amber-400" />
                <Swatch hex="#C08F5F" title="mustard-70" />
                <Swatch hex="#A67A4A" title="mustard-80" />
                <Swatch hex="#8B6539" title="mustard-90" />
                <Swatch hex="#6B4D2A" title="mustard-100" />
              </div>
            </div>

            {/* Charcoal */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-charcoal-900">Charcoal</h3>
                  <p className="text-sm text-charcoal-600">Neutral color - Anchor at 90%</p>
                </div>
                <code className="text-sm font-mono bg-charcoal-100 px-3 py-1 rounded">#2A2A2A</code>
              </div>
              <div className="flex gap-1">
                <Swatch hex="#FFFFFF" title="charcoal-0" />
                <Swatch hex="#F5F5F5" title="charcoal-10" />
                <Swatch hex="#E5E5E5" title="charcoal-20" />
                <Swatch hex="#D1D1D1" title="charcoal-30" />
                <Swatch hex="#A3A3A3" title="charcoal-50" />
                <Swatch hex="#737373" title="charcoal-60" />
                <Swatch hex="#525252" title="charcoal-70" />
                <Swatch hex="#404040" title="charcoal-80" />
                <Swatch hex="#2A2A2A" title="charcoal-90 (anchor)" ringClassName="ring-4 ring-charcoal-400" />
                <Swatch hex="#171717" title="charcoal-100" />
              </div>
            </div>
          </div>
        </div>

      {/* ── ARCHIVE ENTRY: Colors v1 ── June 29, 2026 ── */}
      <div className="border-2 border-dashed border-charcoal-300 overflow-hidden">
        <div className="bg-charcoal-100 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 bg-charcoal-700 text-white text-xs font-mono rounded">ARCHIVED</span>
            <span className="font-bold text-charcoal-900">Colors — v1</span>
          </div>
          <span className="text-sm text-charcoal-500 font-mono">June 29, 2026</span>
        </div>
        <div className="p-6 bg-[var(--bg-raised)] space-y-4">
          <p className="text-charcoal-700 text-sm">
            The standalone <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">/colors</code> page
            was rebuilt on June 29, 2026 to add a dual-mode color context (light app + dark visual/diagram)
            and a semantic color usage map. The palette data below was the complete v1 Colors page content.
          </p>

          <div className="bg-consulting-slate-blue-50 border border-consulting-slate-blue-200 p-4 text-sm">
            <strong className="text-consulting-slate-blue-800">What changed:</strong>
            <ul className="mt-2 space-y-1 text-consulting-slate-blue-700 list-disc list-inside">
              <li>Added Dark Mode / Visual Design context with background tokens and semantic accents</li>
              <li>Added semantic color meaning map (structure → slate, priority → mustard, action → coral, execution → green)</li>
              <li>All brand palettes, extended complements, and structural gap fillers preserved unchanged</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-bold text-charcoal-900 text-sm uppercase tracking-wide">v1 Brand Palette — Anchors</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
              {[
                { name: 'Slate Blue (consulting)', hex: '#3D4B6F', tw: 'consulting-slate-blue-500' },
                { name: 'Sage Green (app primary)', hex: '#5A7D5E', tw: 'slate-blue-500' },
                { name: 'Alpine Green', hex: '#53675B', tw: 'alpine-green-600' },
                { name: 'Granite Gray', hex: '#9CA3AF', tw: 'granite-gray-400' },
                { name: 'Electric Mustard', hex: '#E3B505', tw: 'electric-mustard-600' },
                { name: 'Luminous Coral', hex: '#F85E5E', tw: 'luminous-coral-700' },
                { name: 'Sienna', hex: '#584E47', tw: 'sienna-500' },
              ].map(c => (
                <div key={c.hex} className="flex items-center gap-2 bg-[var(--surface)] rounded p-2">
                  <div className="w-6 h-6 rounded flex-shrink-0 border border-[var(--rule)]" style={{ backgroundColor: c.hex }} />
                  <div>
                    <div className="text-charcoal-800 font-semibold" style={{ fontSize: '10px' }}>{c.name}</div>
                    <div className="text-charcoal-500">{c.hex}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-charcoal-500 italic">
              Extended Complements (Deep Ocean Teal, Terracotta Clay, Sunlit Ochre, Frosted Sky) and
              Structural Gap Fillers (Burnt Orange, Olive Drab Green, Muted Violet Gray, Dusty Sand Beige)
              remain unchanged in the current Colors page.
            </p>
          </div>
        </div>
      </div>

      {/* ── ARCHIVE ENTRY: Typography v1 ── June 29, 2026 ── */}
      <div className="border-2 border-dashed border-charcoal-300 overflow-hidden">
        <div className="bg-charcoal-100 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 bg-charcoal-700 text-white text-xs font-mono rounded">ARCHIVED</span>
            <span className="font-bold text-charcoal-900">Typography — v1</span>
          </div>
          <span className="text-sm text-charcoal-500 font-mono">June 29, 2026</span>
        </div>
        <div className="p-6 bg-[var(--bg-raised)] space-y-4">
          <p className="text-charcoal-700 text-sm">
            The standalone <code className="font-mono text-xs bg-[var(--hover-tint)] px-1 rounded">/typography</code> page
            was rebuilt on June 29, 2026 to reflect the consolidated 4-font system and add loading instructions
            for both Typekit and Google Fonts. Spectral was added as the editorial display font; JetBrains Mono
            replaced Degular Mono as the canonical technical/code font.
          </p>

          <div className="bg-consulting-slate-blue-50 border border-consulting-slate-blue-200 p-4 text-sm">
            <strong className="text-consulting-slate-blue-800">What changed:</strong>
            <ul className="mt-2 space-y-1 text-consulting-slate-blue-700 list-disc list-inside">
              <li>Spectral added as 4th primary font (editorial / display / pull-quote role)</li>
              <li>JetBrains Mono replaces Degular Mono as the canonical code/technical font</li>
              <li>Font loading section added (Typekit kit upa8wrl + Google Fonts)</li>
              <li>Google Docs fallback map added (DM Sans, Work Sans, Spectral, JetBrains Mono)</li>
              <li>Blockquote special treatment updated to use Spectral instead of IBM Plex Serif</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="font-bold text-charcoal-900 text-sm uppercase tracking-wide">v1 Font Stack</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {[
                { role: 'Heading', family: 'fieldwork', source: 'Typekit upa8wrl', status: 'unchanged' },
                { role: 'Body', family: 'degular-variable', source: 'Typekit upa8wrl', status: 'unchanged' },
                { role: 'Body Alt', family: 'freight-text-pro', source: 'Typekit upa8wrl', status: 'unchanged' },
                { role: 'Slab Serif', family: 'yorkten-slab', source: 'Typekit upa8wrl', status: 'unchanged' },
                { role: 'Monospace', family: 'degular-mono', source: 'Typekit upa8wrl', status: 'replaced by JetBrains Mono' },
                { role: 'Display — NEW', family: 'Spectral', source: 'Google Fonts', status: 'added' },
              ].map(f => (
                <div key={f.family} className="flex items-start gap-2 bg-[var(--surface)] rounded p-2 font-mono">
                  <span className={`px-1.5 py-0.5 rounded text-white text-[10px] flex-shrink-0 ${
                    f.status === 'added' ? 'bg-sage-600' :
                    f.status.startsWith('replaced') ? 'bg-[var(--mustard)]' : 'bg-charcoal-400'
                  }`}>{f.status === 'unchanged' ? 'kept' : f.status === 'added' ? 'new' : 'changed'}</span>
                  <div>
                    <div className="text-charcoal-800 font-semibold" style={{ fontSize: '10px' }}>{f.role}</div>
                    <div className="text-charcoal-600">{f.family}</div>
                    <div className="text-charcoal-400" style={{ fontSize: '10px' }}>{f.source}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Archive note */}
      <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-6">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="h-6 w-6 text-[var(--mustard)]" />
          <h3 className="text-lg font-bold text-[var(--mustard)]">About This Archive</h3>
        </div>
        <p className="text-[var(--mustard)]">
          Archived entries are preserved here when a design system page is rebuilt with significant changes.
          Original interactive content (swatches, live previews) is preserved in the version before the change date.
          Each entry records what was present and what changed.
        </p>
      </div>
      </div>
    </div>
  );
};
